// src/components/EmployeeTimeRecords.js
// Individual Employee DTR Page - Monthly View
// Displays an employee's time records for current and previous month

import React, { useState, useEffect, useMemo } from 'react';
import Icon from "./icons/Icon";
import { DayFlagBadge } from "./DayFlagBadge";
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../hooks/useAuth';
import AddTimeRecordModal from './modals/AddTimeRecordModal';
import { loadActiveLocks, deriveLockStatus, isHardLock, FAILCLOSED_LOCK } from '../services/dtrLockDerivation';
import { formatDateString } from '../utils/timeCalculations';
import { toYMDString, manilaYMD } from '../utils/periodDate';
import { isIncompleteRecord } from '../utils/dtrCompleteness';
import { LATE_FILTER_TOKEN, isSearchToken, hasFlaggedRecord, hasAnyFlaggedRecord, dayFlagOf } from '../utils/dayFlags';
import { formatFirestoreTimestamp, getMonthKey, sortRecordsByTimeIn } from '../utils/timeFormatting';
import { formatFilteredCount } from '../utils/footerStats';
import { computeMonthStats } from '../utils/dtrMonthStats';
import { deleteTimeRecord } from '../services/timeRecordService';
import { updateOtApprovalSummary } from '../services/otApprovalSummaryService';
import '../styles/sections/employee-time-records.css';

/**
 * Get the number of days in a month
 * @param {Date} date - Any date in the month
 * @returns {number} Number of days in the month
 */
const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  return new Date(year, month + 1, 0).getDate();
};

/**
 * Generate all dates in a month
 * @param {Date} date - Any date in the month
 * @returns {Date[]} Array of all dates in the month
 */
const getDatesInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const daysInMonth = getDaysInMonth(date);

  return Array.from({ length: daysInMonth }, (_, i) => {
    return new Date(year, month, i + 1);
  });
};

/**
 * Format month label for display
 * @param {Date} date - Date to format
 * @returns {string} Formatted month label (e.g., "October 2025")
 */
const formatMonthLabel = (date) => {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Check if two dates are in the same month and year
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {boolean} True if same month and year
 */
const isSameMonth = (date1, date2) => {
  return date1.getMonth() === date2.getMonth() &&
         date1.getFullYear() === date2.getFullYear();
};

/**
 * Subtract months from a date
 * @param {Date} date - Date to subtract from
 * @param {number} months - Number of months to subtract
 * @returns {Date} The first of the resulting month — stepping from the 31st
 *   with setMonth overflows ("Sep 31" = Oct 1)
 */
const subMonths = (date, months) => new Date(date.getFullYear(), date.getMonth() - months, 1);

/**
 * Add months to a date
 * @param {Date} date - Date to add to
 * @param {number} months - Number of months to add
 * @returns {Date} The first of the resulting month (see subMonths)
 */
const addMonths = (date, months) => new Date(date.getFullYear(), date.getMonth() + months, 1);

/**
 * Format date for display in table — 'YYYY-MM-DD', the app-wide standard
 * (DESIGN-STANDARDS.md §13).
 * @param {Date} date - Date to format
 * @returns {string} e.g. "2025-10-01"
 */
const formatDateForTable = (date) => toYMDString(date);

/**
 * The format this column rendered BEFORE the date standard, e.g. "Oct 01, 2025".
 * Kept for exactly one reason: the search box filters against the RENDERED date,
 * so converting the column alone would silently stop `oct` matching anything.
 * The search haystack now carries both forms, so old muscle memory and the new
 * format both work. Never rendered.
 */
const legacyDateForSearch = (date) => {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric'
  });
};

/**
 * Get day name
 * @param {Date} date - Date to get day name from
 * @returns {string} Day name (e.g., "Monday")
 */
const getDayName = (date) => {
  return date.toLocaleDateString('en-US', { weekday: 'long' });
};

/**
 * The month a linked record belongs to (PRD_InAppNotifications C10), read off its
 * id 'rec_YYYYMMDD_###' — every writer keeps a record's id in its own doc's month.
 * Null when the id doesn't parse or the month is outside the page's window
 * (current and previous month); the page then behaves as it always has.
 * @param {?string} recordId
 * @param {Date} [today]
 * @returns {?Date} the first of that month
 */
export const monthForEditRecord = (recordId, today = new Date()) => {
  const m = /^rec_(\d{4})(\d{2})\d{2}_/.exec(recordId || '');
  if (!m) return null;
  const month = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return isSameMonth(month, thisMonth) || isSameMonth(month, lastMonth) ? month : null;
};

export const EmployeeTimeRecords = () => {
  const { slug, employeeId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, company, hasPermission } = useAuth();
  const canEditTime = hasPermission('time:edit');

  // State management
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  // C10 — a ?editRecord link opens on the record's own month.
  const editRecordParam = searchParams.get('editRecord');
  const [selectedMonth, setSelectedMonth] = useState(() => monthForEditRecord(editRecordParam) || new Date());
  // Counts ?editRecord links as they ARRIVE (not when the page clears one after
  // opening it), so a link re-reads the records even when its month is already on
  // screen — the loaded list may predate the rejection the link is about. A link
  // to another month switches the month in the same render, so the page reads
  // only that month (another employee is a fresh mount: TimeTrackingRoutes keys
  // the page on employeeId).
  const [linkSeq, setLinkSeq] = useState(0);
  const [lastEditRecordParam, setLastEditRecordParam] = useState(editRecordParam);
  if (editRecordParam !== lastEditRecordParam) {
    setLastEditRecordParam(editRecordParam);
    if (editRecordParam) {
      setLinkSeq((n) => n + 1);
      const linkMonth = monthForEditRecord(editRecordParam);
      if (linkMonth && !isSameMonth(selectedMonth, linkMonth)) setSelectedMonth(linkMonth);
    }
  }
  // The linkSeq the loaded records were fetched at; a link opens the edit window
  // only from records fetched after it arrived.
  const [recordsFor, setRecordsFor] = useState(null);
  const [timeRecords, setTimeRecords] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDateForAdd, setSelectedDateForAdd] = useState(new Date());
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  // Derived DTR locks (PRD_DtrWriteModel C1): the payroll-index run entries for this
  // view, loaded once with the records. lockReadFailed → fail-closed (C10a).
  const [lockRuns, setLockRuns] = useState([]);
  const [lockReadFailed, setLockReadFailed] = useState(false);
  // Per DATE, not per record (PRD_TimeRecordAndPayFixes C5a): a run reserves the
  // whole period, so an empty date in it is locked too. lockOf delegates here so a
  // record row and an empty row can never derive differently.
  const lockOfDate = (ymd) =>
    lockReadFailed
      ? FAILCLOSED_LOCK // fail-closed: treat as hard-locked (centralized sentinel, F9)
      : deriveLockStatus(employeeId, ymd, lockRuns);
  const lockOf = (record) => lockOfDate(record.date);
  // Classify a derived lock into the badge predicates — shared by the desktop and
  // mobile renders so they can't drift; isHardLock centralizes the vocabulary (F11/F9).
  const classifyDerivedLock = (lock) => ({
    lock,
    hasSoftLock: lock?.status === 'draft',
    hasHardLock: !!lock && isHardLock(lock.status),
    isPendingLock: lock?.status === 'pending-approval',
  });
  const classifyLock = (record) => classifyDerivedLock(lockOf(record));
  const classifyDate = (ymd) => classifyDerivedLock(lockOfDate(ymd));
  // Desktop lock badge (hard = lock, soft = flag) — one render for record rows and
  // empty rows (C5a).
  const renderLockIcons = ({ lock, hasSoftLock, hasHardLock, isPendingLock }) => (
    <>
      {hasHardLock && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={isPendingLock ? "var(--ui-danger)" : "var(--ui-text-muted)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: "4px", verticalAlign: "middle" }}>
          <title>{isPendingLock ? `Locked by pending payroll ${lock.runId || ''}` : `Locked by Payroll Run ${lock.runId || ''} (Completed)`}</title>
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      )}
      {hasSoftLock && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--ui-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: "4px", verticalAlign: "middle" }}>
          <title>{`Referenced by Draft ${lock.runId || ''}`}</title>
          <line x1="12" y1="5" x2="12" y2="15" />
          <circle cx="12" cy="3" r="2" />
          <path d="M6 15h12l-1 5H7z" />
        </svg>
      )}
    </>
  );
  // Mobile lock labels — one render for record cards and empty-date cards (C5a).
  const renderMobileLockLabels = ({ lock, hasSoftLock, hasHardLock, isPendingLock }) => (
    <>
      {hasHardLock && (
        <div
          style={{
            gridColumn: "1 / -1",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "0.75rem",
            color: isPendingLock ? "var(--ui-danger)" : "var(--ui-text-muted)",
            fontWeight: "500",
            marginBottom: "0.25rem",
          }}
        >
          <Icon name="lock" size={12} />
          {isPendingLock
            ? `Pending payroll ${lock.runId || ''}`
            : `Locked — Payroll ${lock.runId || ''}`}
        </div>
      )}
      {hasSoftLock && (
        <div
          style={{
            gridColumn: "1 / -1",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "0.75rem",
            color: "var(--ui-warning)",
            fontWeight: "500",
            marginBottom: "0.25rem",
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--ui-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="15" />
            <circle cx="12" cy="3" r="2" />
            <path d="M6 15h12l-1 5H7z" />
          </svg>
          Draft {lock.runId || ''}
        </div>
      )}
    </>
  );

  // New state for UI redesign
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Payroll rates (financial rates only)
  const [payrollRates, setPayrollRates] = useState(null);
  const [payrollRatesError, setPayrollRatesError] = useState(null);

  // Attendance policy for ND window, defaults, and leeway
  const [attendancePolicy, setAttendancePolicy] = useState(null);

  // Handle responsive view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Calculate navigation constraints
  const today = new Date();
  const oneMonthAgo = subMonths(today, 1);
  const isNextDisabled = isSameMonth(selectedMonth, today);
  const isPreviousDisabled = isSameMonth(selectedMonth, oneMonthAgo);

  // Fetch employee data
  useEffect(() => {
    const fetchEmployee = async () => {
      if (!company?.id || !employeeId) return;

      try {
        // Query employees by employeeId
        const employeesRef = collection(db, 'companies', company.id, 'employees');
        const q = query(employeesRef, where('employeeId', '==', employeeId));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const employeeDoc = snapshot.docs[0];
          setEmployee({ id: employeeDoc.id, ...employeeDoc.data() });
        } else {
          setError('Employee not found');
        }
      } catch (err) {
        console.error('Error fetching employee:', err);
        setError('Failed to load employee data');
      }
    };

    fetchEmployee();
  }, [company?.id, employeeId]);

  // Fetch payroll rates (financial) and attendance policy (ND, defaults, leeway)
  useEffect(() => {
    const fetchSettings = async () => {
      if (!company?.id) return;

      try {
        // Fetch payroll rates (financial rates)
        const ratesDoc = await getDoc(doc(db, 'companies', company.id, 'settings', 'payrollRates'));
        if (ratesDoc.exists()) {
          setPayrollRates(ratesDoc.data());
        }

        // Fetch attendance policy (ND window, defaults, leeway)
        const policyDoc = await getDoc(doc(db, 'companies', company.id, 'settings', 'attendancePolicy'));
        if (policyDoc.exists()) {
          const policyData = policyDoc.data();
          if (typeof policyData.nightDiffStart === 'number' && typeof policyData.nightDiffEnd === 'number') {
            setAttendancePolicy(policyData);
            setPayrollRatesError(null);
          } else {
            setPayrollRatesError('Night Differential settings are missing. Please configure them in Settings > Attendance Policy.');
          }
        } else {
          setPayrollRatesError('Attendance policy not configured. Please set it up in Settings > Attendance Policy.');
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
        setPayrollRatesError('Failed to load settings.');
      }
    };

    fetchSettings();
  }, [company?.id]);

  // Fetch monthly time records
  useEffect(() => {
    const fetchTimeRecords = async () => {
      if (!company?.id || !employeeId) return;

      const fetchedFor = linkSeq;
      setLoading(true);
      try {
        const monthId = getMonthKey(selectedMonth);
        const docId = `${monthId}_${employeeId}`;

        // Fetch current month document
        const docRef = doc(db, 'companies', company.id, 'dailyTimeRecords', docId);
        const docSnap = await getDoc(docRef);

        let currentMonthRecords = [];
        if (docSnap.exists()) {
          const data = docSnap.data();
          currentMonthRecords = data.timeRecords || [];
        }

        setTimeRecords(currentMonthRecords);
        setRecordsFor(fetchedFor);
        setError(null);

        // Derived DTR locks (C1): load the payroll index once for this view. A read
        // error sets fail-closed (C10a) — records render locked, never editable.
        try {
          setLockRuns(await loadActiveLocks(company.id));
          setLockReadFailed(false);
        } catch (lockErr) {
          console.error('[EmployeeTimeRecords] lock index read failed — fail-closed:', lockErr);
          setLockRuns([]);
          setLockReadFailed(true);
        }
      } catch (err) {
        console.error('Error fetching time records:', err);
        setError('Failed to load time records');
        setTimeRecords([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTimeRecords();
  }, [company?.id, employeeId, selectedMonth, linkSeq]);

  // Auto-open edit modal when ?editRecord=xxx is in the URL
  useEffect(() => {
    const editRecordId = searchParams.get('editRecord');
    if (!editRecordId || !canEditTime || timeRecords.length === 0 || recordsFor !== linkSeq) return;

    const record = timeRecords.find(r => r.id === editRecordId);
    if (record) {
      setEditingRecord(record);
      setShowEditModal(true);
      // Clear the param so refreshing doesn't re-open
      setSearchParams({}, { replace: true });
    }
  }, [timeRecords, searchParams, canEditTime, setSearchParams, recordsFor, linkSeq]);

  // The edit window's date, stable across re-renders: the modal re-initialises its
  // Time In / Time Out dates whenever this prop changes, which on an overnight
  // record moved Time Out back onto the Time In day.
  const editSelectedDate = useMemo(
    () => (editingRecord ? new Date(editingRecord.date) : new Date()),
    [editingRecord]
  );

  // Process records for display
  const displayData = useMemo(() => {
    const allDates = getDatesInMonth(selectedMonth);
    const recordsMap = {};

    // Map records to dates
    timeRecords.forEach(record => {
      if (!recordsMap[record.date]) {
        recordsMap[record.date] = [];
      }
      recordsMap[record.date].push(record);
    });

    // Map each date to its records (each record stays on its clock-in date)
    let sortedData = allDates.map(date => {
      const dateStr = formatDateString(date);
      // Chronological within the day — the stored array is in entry order.
      const records = sortRecordsByTimeIn(recordsMap[dateStr]);

      return {
        date: dateStr,
        dateObj: date,
        records
      };
    });

    // Apply sorting (following TimeTracking pattern)
    if (sortField) {
      sortedData = [...sortedData].sort((a, b) => {
        // FLAGS sort (C4): ANY flagged day first — late, piece rate, either
        // leave, or absent — tiebroken by date ascending. This supersedes the
        // late-only sort from CL-002 #6: a header labelled FLAGS that sorted
        // only by lateness is a false affordance. The `late` search token and
        // the Late Only dropdown option remain the precise late-only tools.
        // Descending inverts to unflagged-first; the date tiebreak stays ascending.
        if (sortField === "late") {
          const aFlagged = hasAnyFlaggedRecord(a.records);
          const bFlagged = hasAnyFlaggedRecord(b.records);
          if (aFlagged !== bFlagged) {
            return sortDirection === "asc" ? (aFlagged ? -1 : 1) : (aFlagged ? 1 : -1);
          }
          return a.dateObj.getTime() - b.dateObj.getTime();
        }

        let aValue, bValue;

        switch (sortField) {
          case "date":
            aValue = a.dateObj.getTime();
            bValue = b.dateObj.getTime();
            break;
          case "otHours":
            aValue = a.records.reduce((sum, r) => sum + (r.overtimeHours || 0), 0);
            bValue = b.records.reduce((sum, r) => sum + (r.overtimeHours || 0), 0);
            break;
          case "nightDiff":
            aValue = a.records.reduce((sum, r) => sum + (r.nightDiffHours || 0), 0);
            bValue = b.records.reduce((sum, r) => sum + (r.nightDiffHours || 0), 0);
            break;
          case "utHours":
            aValue = a.records.reduce((sum, r) => sum + (r.undertimeHours || 0), 0);
            bValue = b.records.reduce((sum, r) => sum + (r.undertimeHours || 0), 0);
            break;
          default:
            aValue = 0;
            bValue = 0;
        }

        if (sortDirection === "asc") {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });
    }

    return sortedData;
  }, [selectedMonth, timeRecords, sortField, sortDirection]);

  // Filter display data by search and filter type
  const filteredDisplayData = useMemo(() => {
    let result = [...displayData];

    // Search filter. Typing a day-type token — `late`, `piece rate`,
    // `paid leave`, `unpaid leave`, `absent`, or `leave` for either kind —
    // switches the box from date matching to flag filtering (BL-006, C8),
    // across the SELECTED MONTH. Matching is by PREFIX, so a partial term
    // narrows as it is typed and a token need not be completed.
    //
    // The prefix costs one keystroke against date search: `a` prefixes
    // `absent`, so searching `Aug` filters to absent days until the `u` lands.
    // Accepted at /test-impl — it self-corrects on the next character, and the
    // exact test it replaced left the table empty for the whole of a nine-
    // character token.
    if (searchTerm) {
      if (isSearchToken(searchTerm)) {
        result = result.filter(({ records }) => hasFlaggedRecord(records, searchTerm));
      } else {
        const term = searchTerm.toLowerCase();
        result = result.filter(({ dateObj }) => {
          // Both forms: the rendered '2025-10-01' and the pre-standard
          // 'Oct 01, 2025'. Matching only the rendered one would have made
          // `oct` return nothing the day the column changed format.
          const ymd = formatDateForTable(dateObj).toLowerCase();
          const legacy = legacyDateForSearch(dateObj).toLowerCase();
          return ymd.includes(term) || legacy.includes(term);
        });
      }
    }

    // Entry type filter. Every day-type option's value IS its search token, so
    // the picked route and the typed route resolve through one predicate rather
    // than two that can drift (C7).
    if (isSearchToken(filterType)) {
      result = result.filter(({ records }) => hasFlaggedRecord(records, filterType));
    } else if (filterType === "with-entries") {
      result = result.filter(({ records }) => records.length > 0);
    } else if (filterType === "no-entries") {
      result = result.filter(({ records }) => records.length === 0);
    } else if (filterType === "incomplete") {
      // Days holding a record missing exactly one of time-in/time-out — PRD_DtrCompletenessGuard
      // C5. Record-level, so a complete overnight/ND pair (one record with both times) is never
      // flagged; placeholders AND no-times/empty rows derive complete too (also never flagged).
      result = result.filter(({ records }) => records.some((r) => isIncompleteRecord(r)));
    }

    return result;
  }, [displayData, searchTerm, filterType]);

  // Filter-aware footer stats (PRD_FilterAwareFooterStats C1/C3): the figures
  // cover the FILTERED rows the table renders; Total Days carries the dual
  // "shown of month-length" form. The predicate matches the untrimmed
  // conditions the filter memo above tests; selectedMonth is a scope selector,
  // not a filter.
  const filterActive = Boolean(searchTerm) || Boolean(filterType);
  const monthlyStats = useMemo(() => {
    // Bucket semantics (day-scoped counts, overlapping buckets, record-scoped
    // hour sums) live in the shared computeMonthStats — extracted so the DTR
    // report cannot fork them (PRD_DtrReport C14). The screen renders eight of
    // its counters plus Regular Hours; Late is report-only (C13).
    const stats = computeMonthStats(filteredDisplayData);

    return {
      // Only the PRIMARY stat carries the dual '{filtered} of {total}' form
      // (FAFS C1); every secondary figure recomputes over the filtered rows and
      // renders plain.
      totalDays: formatFilteredCount(
        filteredDisplayData.length,
        displayData.length,
        filterActive
      ),
      daysWorked: stats.daysWorked,
      pieceRateDays: stats.pieceRateDays,
      leaveDays: stats.leaveDays,
      absentDays: stats.absentDays,
      totalOTHours: stats.totalOTHours.toFixed(2),
      totalNightDiff: stats.totalNightDiff.toFixed(2),
      totalUtHours: stats.totalUtHours.toFixed(2),
      totalRegularHours: stats.totalRegularHours.toFixed(2)
    };
  }, [filteredDisplayData, displayData, filterActive]);

  // Navigation handlers
  const handlePreviousMonth = () => {
    if (isPreviousDisabled) return;
    const newMonth = subMonths(selectedMonth, 1);
    setSelectedMonth(newMonth);
  };

  const handleNextMonth = () => {
    if (isNextDisabled) return;
    const newMonth = addMonths(selectedMonth, 1);
    setSelectedMonth(newMonth);
  };

  const handleBackToTimeTracking = () => {
    navigate(`/company/${slug}/time-tracking`);
  };

  // Sort handler (same as TimeTracking)
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Sort Icon Component
  const SortIcon = ({ field }) => {
    const isActive = sortField === field;
    const color = "white";

    if (isActive && sortDirection === "asc") {
      return (
        <Icon name="arrow-up" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />
      );
    }

    if (isActive && sortDirection === "desc") {
      return (
        <Icon name="arrow-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />
      );
    }

    return (
      <Icon name="chevrons-up-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle", opacity: 0.5 }} />
    );
  };

  const handleAddTimeRecord = (date = null) => {
    if (!canEditTime) return;
    if (date) {
      setSelectedDateForAdd(date);
    } else {
      setSelectedDateForAdd(new Date());
    }
    setShowAddModal(true);
  };

  const handleSaveSuccess = async () => {
    // Refresh time records after save
    const monthId = getMonthKey(selectedMonth);
    const docId = `${monthId}_${employeeId}`;
    const docRef = doc(db, 'companies', company.id, 'dailyTimeRecords', docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      setTimeRecords(data.timeRecords || []);
    }

    setShowAddModal(false);
    setShowEditModal(false);
    setEditingRecord(null);
  };

  const handleRowClick = (record) => {
    // Don't open modal for empty records (no id) or viewers
    if (!canEditTime) return;
    if (!record || !record.id) return;

    setEditingRecord(record);
    setShowEditModal(true);
  };

  const handleDeleteRecord = async (recordId) => {
    try {
      // Capture OT approval status before deletion for summary update
      const oldOtStatus = editingRecord?.otApproval?.status || null;

      await deleteTimeRecord(company.id, employeeId, recordId);

      // Update OT approval summary if the deleted record had an otApproval status
      if (oldOtStatus) {
        updateOtApprovalSummary(company.id, oldOtStatus, null).catch(console.error);
      }

      // Close modal
      setShowEditModal(false);
      setEditingRecord(null);

      // Refresh data
      await handleSaveSuccess();
    } catch (error) {
      console.error('Error deleting time record:', error);
      alert('Failed to delete time record: ' + error.message);
    }
  };

  // Format record display — single row per record, +1 badge for overnight time-out
  const formatRecordDisplay = (record) => {
    const isOvernight = record.timeOut &&
      record.timeOut.toDate && manilaYMD(record.timeOut.toDate()) !== record.date; // Manila day (C3)

    return {
      timeIn: formatFirestoreTimestamp(record.timeIn) || '—',
      timeOut: formatFirestoreTimestamp(record.timeOut) || '—',
      break: record.breakMinutes ? `${record.breakMinutes} min` : '—',
      regularHours: record.regularHours?.toFixed(2) || '—',
      otHours: record.overtimeHours?.toFixed(2) || '—',
      nightDiff: record.nightDiffHours?.toFixed(2) || '—',
      utHours: (record.undertimeHours || 0).toFixed(2),
      // The single day-type flag this record carries, or null (C1). Resolved
      // once here so the desktop row and the mobile card cannot disagree.
      dayFlag: dayFlagOf(record),
      isOvernight,
      timeOutDate: isOvernight ? record.timeOut.toDate() : null
    };
  };

  // Loading state
  if (loading && !employee) {
    return (
      <div className="employee-time-records-container">
        <div className="etr-loading">
          <div className="etr-loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !employee) {
    return (
      <div className="employee-time-records-container">
        <div className="etr-empty-state">
          <div className="etr-empty-icon">
            <Icon name="alert-triangle" style={{ width: "48px", height: "48px", stroke: "var(--ui-danger)" }} />
          </div>
          <h3 className="etr-empty-title">Error Loading Data</h3>
          <p className="etr-empty-text">{error}</p>
          <button
            className="btn-primary"
            onClick={handleBackToTimeTracking}
          >
            Back to Time Tracking
          </button>
        </div>
      </div>
    );
  }

  if (!employee) return null;

  const lastName = employee.personalInfo?.lastName || '';
  const firstName = employee.personalInfo?.firstName || '';
  const fullName = lastName && firstName
    ? `${lastName}, ${firstName}`
    : lastName || firstName || 'Unknown Employee';
  const initials = `${employee.personalInfo?.firstName?.charAt(0) || ''}${employee.personalInfo?.lastName?.charAt(0) || ''}`.toUpperCase() || '??';
  const department = employee.employment?.department || 'Not set';

  return (
    <div style={{ fontFamily: "var(--font-family-primary)" }}>
      <div style={{ width: "100%" }}>
        {/* Page Title */}
        <h1
          style={{
            fontSize: isMobile ? "1.125rem" : "1.25rem",
            fontWeight: "700",
            color: "var(--ui-primary)",
            margin: isMobile ? "0 0 1rem 0" : "0 0 0.75rem 0",
          }}
        >
          Employee Time Records
        </h1>

        {/* Compact Header Row - Back + Employee Identity + Add Button */}
        {!isMobile && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "1rem",
              padding: "0.75rem 1rem",
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              marginBottom: "0.75rem",
            }}
          >
            <button
              onClick={handleBackToTimeTracking}
              style={{
                padding: "0.5rem 0.875rem",
                background: "white",
                color: "var(--ui-text-secondary)",
                border: "1px solid var(--ui-border)",
                borderRadius: "8px",
                fontWeight: "500",
                cursor: "pointer",
                fontSize: "0.8125rem",
                display: "flex",
                alignItems: "center",
                gap: "0.375rem",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              <Icon name="arrow-left" size={14} />
              Back
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flex: 1, minWidth: 0 }}>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  background: "var(--ui-primary-tint)",
                  color: "var(--ui-primary)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "600",
                  fontSize: "13px",
                  flexShrink: 0,
                }}
              >
                {initials}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", minWidth: 0 }}>
                <span style={{ fontSize: "0.9375rem", fontWeight: "600", color: "var(--ui-text)", whiteSpace: "nowrap" }}>
                  {fullName}
                </span>
                <span style={{ color: "var(--ui-border)", fontSize: "0.5rem" }}>&#9679;</span>
                <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "0.75rem", color: "var(--ui-text-muted)" }}>
                  {employeeId}
                </span>
                <span style={{ color: "var(--ui-border)", fontSize: "0.5rem" }}>&#9679;</span>
                <span style={{ fontSize: "0.8125rem", color: "var(--ui-text-muted)" }}>
                  {department}
                </span>
              </div>
            </div>
            {canEditTime && (
              <button
                onClick={() => handleAddTimeRecord()}
                style={{
                  padding: "0.5rem 1rem",
                  background: "var(--ui-primary)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontSize: "0.8125rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.375rem",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                <Icon name="plus" size={14} />
                Add Time Record
              </button>
            )}
          </div>
        )}

        {/* Mobile Header - Back + Employee Identity */}
        {isMobile && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexDirection: "column",
                gap: "1rem",
                marginBottom: "1rem",
              }}
            >
              <button
                onClick={handleBackToTimeTracking}
                style={{
                  padding: "0.75rem 1.25rem",
                  background: "white",
                  color: "var(--ui-text-secondary)",
                  border: "1px solid var(--ui-border)",
                  borderRadius: "8px",
                  fontWeight: "500",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <Icon name="arrow-left" size={14} aria-hidden="true" /> Back to Time Tracking
              </button>
            </div>

            {/* Mobile Employee Info Card */}
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "1rem",
                marginBottom: "1rem",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                display: "flex",
                alignItems: "center",
                gap: "1rem",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  background: "var(--ui-primary-tint)",
                  color: "var(--ui-primary)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "600",
                  fontSize: "1rem",
                  flexShrink: 0,
                }}
              >
                {initials}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                <div style={{ fontSize: "1rem", fontWeight: "600", color: "var(--ui-text)" }}>
                  {fullName}
                </div>
                <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "0.75rem", color: "var(--ui-text-muted)" }}>
                    {employeeId}
                  </span>
                  <span style={{ color: "var(--ui-text-muted)", fontSize: "0.75rem", fontWeight: "500" }}>
                    {department}
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile Stats Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "1rem",
                marginBottom: "1rem",
              }}
            >
              <div style={{ background: "white", borderRadius: "8px", padding: "1.25rem 1.25rem 1.25rem 1.5rem", border: "1px solid var(--ui-border)", borderLeft: "none", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", left: 0, top: "15%", bottom: "15%", width: "4px", background: "var(--ui-primary)", borderRadius: "0 4px 4px 0" }} />
                <div style={{ fontSize: "0.6875rem", color: "var(--ui-text-muted)", textTransform: "uppercase", fontWeight: "600", letterSpacing: "0.025em", marginBottom: "0.25rem" }}>Total Days</div>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-text)" }}>{monthlyStats.totalDays}</div>
              </div>
              <div style={{ background: "white", borderRadius: "8px", padding: "1.25rem 1.25rem 1.25rem 1.5rem", border: "1px solid var(--ui-border)", borderLeft: "none", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", left: 0, top: "15%", bottom: "15%", width: "4px", background: "var(--ui-success)", borderRadius: "0 4px 4px 0" }} />
                <div style={{ fontSize: "0.6875rem", color: "var(--ui-text-muted)", textTransform: "uppercase", fontWeight: "600", letterSpacing: "0.025em", marginBottom: "0.25rem" }}>Days Worked</div>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-text)" }}>{monthlyStats.daysWorked}</div>
              </div>
              <div style={{ background: "white", borderRadius: "8px", padding: "1.25rem 1.25rem 1.25rem 1.5rem", border: "1px solid var(--ui-border)", borderLeft: "none", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", left: 0, top: "15%", bottom: "15%", width: "4px", background: "var(--ui-success-text)", borderRadius: "0 4px 4px 0" }} />
                <div style={{ fontSize: "0.6875rem", color: "var(--ui-text-muted)", textTransform: "uppercase", fontWeight: "600", letterSpacing: "0.025em", marginBottom: "0.25rem" }}>Piece Rate</div>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-text)" }}>{monthlyStats.pieceRateDays}</div>
              </div>
              <div style={{ background: "white", borderRadius: "8px", padding: "1.25rem 1.25rem 1.25rem 1.5rem", border: "1px solid var(--ui-border)", borderLeft: "none", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", left: 0, top: "15%", bottom: "15%", width: "4px", background: "var(--ui-text-muted)", borderRadius: "0 4px 4px 0" }} />
                <div style={{ fontSize: "0.6875rem", color: "var(--ui-text-muted)", textTransform: "uppercase", fontWeight: "600", letterSpacing: "0.025em", marginBottom: "0.25rem" }}>Leave</div>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-text)" }}>{monthlyStats.leaveDays}</div>
              </div>
              <div style={{ background: "white", borderRadius: "8px", padding: "1.25rem 1.25rem 1.25rem 1.5rem", border: "1px solid var(--ui-border)", borderLeft: "none", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", left: 0, top: "15%", bottom: "15%", width: "4px", background: "var(--ui-danger)", borderRadius: "0 4px 4px 0" }} />
                <div style={{ fontSize: "0.6875rem", color: "var(--ui-text-muted)", textTransform: "uppercase", fontWeight: "600", letterSpacing: "0.025em", marginBottom: "0.25rem" }}>Absent</div>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-text)" }}>{monthlyStats.absentDays}</div>
              </div>
              <div style={{ background: "white", borderRadius: "8px", padding: "1.25rem 1.25rem 1.25rem 1.5rem", border: "1px solid var(--ui-border)", borderLeft: "none", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", left: 0, top: "15%", bottom: "15%", width: "4px", background: "var(--ui-warning)", borderRadius: "0 4px 4px 0" }} />
                <div style={{ fontSize: "0.6875rem", color: "var(--ui-text-muted)", textTransform: "uppercase", fontWeight: "600", letterSpacing: "0.025em", marginBottom: "0.25rem" }}>OT Hours</div>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-text)" }}>{monthlyStats.totalOTHours}</div>
              </div>
              <div style={{ background: "var(--ui-primary)", borderRadius: "8px", padding: "1.25rem" }}>
                <div style={{ fontSize: "0.6875rem", color: "rgba(255,255,255,0.8)", textTransform: "uppercase", fontWeight: "600", letterSpacing: "0.025em", marginBottom: "0.25rem" }}>Total Night Diff</div>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "white" }}>{monthlyStats.totalNightDiff}</div>
              </div>
              <div style={{ background: "white", borderRadius: "8px", padding: "1.25rem 1.25rem 1.25rem 1.5rem", border: "1px solid var(--ui-border)", borderLeft: "none", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", left: 0, top: "15%", bottom: "15%", width: "4px", background: "var(--ui-danger)", borderRadius: "0 4px 4px 0" }} />
                <div style={{ fontSize: "0.6875rem", color: "var(--ui-text-muted)", textTransform: "uppercase", fontWeight: "600", letterSpacing: "0.025em", marginBottom: "0.25rem" }}>UT Hours</div>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-danger)" }}>{monthlyStats.totalUtHours}</div>
              </div>
              <div style={{ background: "white", borderRadius: "8px", padding: "1.25rem 1.25rem 1.25rem 1.5rem", border: "1px solid var(--ui-border)", borderLeft: "none", position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", left: 0, top: "15%", bottom: "15%", width: "4px", background: "var(--ui-primary)", borderRadius: "0 4px 4px 0" }} />
                <div style={{ fontSize: "0.6875rem", color: "var(--ui-text-muted)", textTransform: "uppercase", fontWeight: "600", letterSpacing: "0.025em", marginBottom: "0.25rem" }}>Regular Hours</div>
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--ui-text)" }}>{monthlyStats.totalRegularHours}</div>
              </div>
            </div>

            {/* Mobile Controls Section */}
            <div
              style={{
                background: "white",
                padding: "1rem",
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                marginBottom: "1rem",
              }}
            >
              {canEditTime && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "stretch", flexDirection: "column", gap: "1rem", marginBottom: "1rem" }}>
                  <button
                    onClick={() => handleAddTimeRecord()}
                    style={{
                      backgroundColor: "var(--ui-primary)",
                      color: "white",
                      padding: "0.75rem 1.25rem",
                      border: "none",
                      borderRadius: "8px",
                      fontWeight: "600",
                      cursor: "pointer",
                      fontSize: "0.875rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      whiteSpace: "nowrap",
                      width: "100%",
                      justifyContent: "center",
                    }}
                  >
                    + Add Time Record
                  </button>
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", paddingBottom: "1rem", marginBottom: "1rem", borderBottom: "1px solid var(--ui-border)", flexWrap: "wrap" }}>
                <button onClick={handlePreviousMonth} disabled={isPreviousDisabled} style={{ padding: "0.5rem 0.75rem", background: "white", border: "1px solid var(--ui-border)", borderRadius: "6px", cursor: isPreviousDisabled ? "not-allowed" : "pointer", opacity: isPreviousDisabled ? 0.5 : 1, fontSize: "0.875rem" }}>&lt;</button>
                <button onClick={handleNextMonth} disabled={isNextDisabled} style={{ padding: "0.5rem 0.75rem", background: "white", border: "1px solid var(--ui-border)", borderRadius: "6px", cursor: isNextDisabled ? "not-allowed" : "pointer", opacity: isNextDisabled ? 0.5 : 1, fontSize: "0.875rem" }}>&gt;</button>
                <span style={{ marginLeft: "0.5rem", fontWeight: "500", color: "var(--ui-text-secondary)", fontSize: "0.875rem" }}>{formatMonthLabel(selectedMonth)}</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1rem" }}>
                <input type="text" placeholder="Search by date (Jan 03, 2026) or a flag type..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ width: "100%", padding: "0.75rem", border: "1px solid var(--ui-border)", borderRadius: "6px", fontSize: "0.875rem" }} />
                <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={{ width: "100%", padding: "0.75rem", border: "1px solid var(--ui-border)", borderRadius: "6px", fontSize: "0.875rem", background: "white", cursor: "pointer" }}>
                  <option value="">All Records</option>
                  <option value="with-entries">With Time Entries</option>
                  <option value="no-entries">No Time Entries</option>
                  <option value="incomplete">Incomplete (missing in/out)</option>
                  <option value={LATE_FILTER_TOKEN}>Late Only</option>
                  <option value="piece rate">Piece Rate Only</option>
                  <option value="paid leave">Paid Leave Only</option>
                  <option value="unpaid leave">Unpaid Leave Only</option>
                  <option value="absent">Absent Only</option>
                </select>
              </div>
            </div>
          </>
        )}

        {/* Desktop Compact Controls Bar - Month Nav + Search + Filter */}
        {!isMobile && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "0.625rem 1rem",
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              marginBottom: "0.75rem",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.375rem", flexShrink: 0 }}>
              <button
                onClick={handlePreviousMonth}
                disabled={isPreviousDisabled}
                style={{
                  padding: "0.375rem 0.625rem",
                  background: "white",
                  border: "1px solid var(--ui-border)",
                  borderRadius: "6px",
                  cursor: isPreviousDisabled ? "not-allowed" : "pointer",
                  opacity: isPreviousDisabled ? 0.5 : 1,
                  fontSize: "0.8125rem",
                  color: "var(--ui-text-secondary)",
                }}
              >
                &lt;
              </button>
              <button
                onClick={handleNextMonth}
                disabled={isNextDisabled}
                style={{
                  padding: "0.375rem 0.625rem",
                  background: "white",
                  border: "1px solid var(--ui-border)",
                  borderRadius: "6px",
                  cursor: isNextDisabled ? "not-allowed" : "pointer",
                  opacity: isNextDisabled ? 0.5 : 1,
                  fontSize: "0.8125rem",
                  color: "var(--ui-text-secondary)",
                }}
              >
                &gt;
              </button>
              <span style={{ fontWeight: "500", color: "var(--ui-text-secondary)", fontSize: "0.875rem", marginLeft: "0.25rem", whiteSpace: "nowrap" }}>
                {formatMonthLabel(selectedMonth)}
              </span>
            </div>
            <div style={{ width: "1px", height: "24px", background: "var(--ui-border)", flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Search by date (Jan 03, 2026) or a flag type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                minWidth: "160px",
                padding: "0.5rem 0.75rem",
                border: "1px solid var(--ui-border)",
                borderRadius: "6px",
                fontSize: "0.8125rem",
                fontFamily: "inherit",
              }}
            />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                minWidth: "140px",
                padding: "0.5rem 0.75rem",
                border: "1px solid var(--ui-border)",
                borderRadius: "6px",
                fontSize: "0.8125rem",
                background: "white",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <option value="">All Records</option>
              <option value="with-entries">With Time Entries</option>
              <option value="no-entries">No Time Entries</option>
              <option value="incomplete">Incomplete (missing in/out)</option>
              <option value={LATE_FILTER_TOKEN}>Late Only</option>
              <option value="piece rate">Piece Rate Only</option>
              <option value="paid leave">Paid Leave Only</option>
              <option value="unpaid leave">Unpaid Leave Only</option>
              <option value="absent">Absent Only</option>
            </select>
          </div>
        )}

        {/* C5(d): a failed lock read fails closed, which locks every row with no run
            ID to show — say why instead of letting the rows imply the dates are paid. */}
        {!loading && lockReadFailed && (
          <div
            role="alert"
            style={{
              marginBottom: "0.75rem",
              padding: "10px 12px",
              borderRadius: "8px",
              background: "var(--ui-warning-tint)",
              color: "var(--ui-warning-text)",
              fontSize: "0.875rem",
            }}
          >
            Couldn't check payroll locks — dates can't be edited until you reload.
          </div>
        )}

        {/* Time Records Table or Empty State */}
        {loading ? (
          <div className="etr-loading-records">
            <div className="etr-loading-spinner"></div>
          </div>
        ) : filteredDisplayData.length === 0 ? (
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              overflow: "hidden",
            }}
          >
            {/* Blue Header Bar */}
            <div
              style={{
                background: "var(--ui-gradient-primary)",
                padding: "14px 16px",
              }}
            >
              <span
                style={{
                  color: "white",
                  fontWeight: "600",
                  fontSize: "0.875rem",
                }}
              >
                Monthly Time Records
              </span>
            </div>
            {/* Empty State Content */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "4rem 2rem",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: "100px",
                  height: "100px",
                  background: "var(--ui-primary-tint)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1.5rem",
                  border: "3px solid var(--ui-primary)",
                }}
              >
                <Icon name="calendar" style={{ width: "48px", height: "48px", stroke: "var(--ui-primary)" }} />
              </div>
              <h3
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  color: "var(--ui-primary)",
                  marginBottom: "0.5rem",
                }}
              >
                No Time Records
              </h3>
              <p
                style={{
                  color: "var(--ui-text-muted)",
                  marginBottom: "0",
                  lineHeight: "1.6",
                  maxWidth: "400px",
                  fontSize: "0.9375rem",
                }}
              >
                {searchTerm || filterType ? (
                  <>No records match your search criteria.</>
                ) : (
                  <>
                    No time records found for{" "}
                    <span style={{ fontWeight: "600", color: "var(--ui-text-secondary)" }}>
                      {formatMonthLabel(selectedMonth)}
                    </span>
                    . Click "Add Time Record" to create the first entry for this employee.
                  </>
                )}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                overflow: "hidden",
                display: isMobile ? "none" : "block",
              }}
            >
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
                  <thead>
                    <tr style={{ background: "var(--ui-gradient-primary)" }}>
                      <th
                        onClick={() => handleSort("date")}
                        style={{
                          padding: "14px 16px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          color: "white",
                          cursor: "pointer",
                          userSelect: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Date{<SortIcon field="date" />}
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          color: "white",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Time In
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          color: "white",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Time Out
                      </th>
                      <th
                        onClick={() => handleSort("late")}
                        style={{
                          padding: "14px 16px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          color: "white",
                          cursor: "pointer",
                          userSelect: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Flags{<SortIcon field="late" />}
                      </th>
                      <th
                        onClick={() => handleSort("otHours")}
                        style={{
                          padding: "14px 16px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          color: "white",
                          cursor: "pointer",
                          userSelect: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        OT Hours{<SortIcon field="otHours" />}
                      </th>
                      <th
                        onClick={() => handleSort("nightDiff")}
                        style={{
                          padding: "14px 16px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          color: "white",
                          cursor: "pointer",
                          userSelect: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Night Diff{<SortIcon field="nightDiff" />}
                      </th>
                      <th
                        onClick={() => handleSort("utHours")}
                        style={{
                          padding: "14px 16px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          color: "white",
                          cursor: "pointer",
                          userSelect: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        UT Hours{<SortIcon field="utHours" />}
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          color: "white",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Regular Hours
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          color: "white",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Break
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDisplayData.map(({ date, dateObj, records }) => {
                      const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;
                      const dayAbbr = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                      const weekendBg = isWeekend ? "var(--ui-surface-recessed)" : "transparent";

                      if (records.length === 0) {
                        // C5(a): an empty date inside a run carries the same badge as a
                        // record row; a hard-locked one cannot be clicked into an add.
                        const dateLock = classifyDate(date);
                        const canAdd = !dateLock.hasHardLock;
                        return (
                          <tr
                            key={date}
                            data-testid={`etr-empty-row-${date}`}
                            onClick={canAdd ? () => handleAddTimeRecord(dateObj) : undefined}
                            style={{
                              borderBottom: "1px solid var(--ui-surface-recessed)",
                              opacity: 0.6,
                              cursor: canAdd ? "pointer" : "not-allowed",
                              backgroundColor: weekendBg,
                              transition: "background-color 0.2s, opacity 0.2s",
                            }}
                            onMouseEnter={canAdd ? (e) => {
                              e.currentTarget.style.backgroundColor = "var(--ui-primary-tint)";
                              e.currentTarget.style.opacity = "0.85";
                            } : undefined}
                            onMouseLeave={canAdd ? (e) => {
                              e.currentTarget.style.backgroundColor = weekendBg;
                              e.currentTarget.style.opacity = "0.6";
                            } : undefined}
                          >
                            <td style={{ padding: "16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)", whiteSpace: "nowrap" }}>
                              {formatDateForTable(dateObj)}
                              <span style={{ fontSize: "12px", color: "var(--ui-text-faint)", fontWeight: "400", marginLeft: "8px" }}>{dayAbbr}</span>
                              {canAdd && <span className="etr-add-icon">+</span>}
                            </td>
                            <td style={{ padding: "16px", fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>
                              {renderLockIcons(dateLock)}
                              —
                            </td>
                            <td style={{ padding: "16px", fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>—</td>
                            <td style={{ padding: "16px", fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>—</td>
                            <td style={{ padding: "16px", fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>—</td>
                            <td style={{ padding: "16px", fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>—</td>
                            <td style={{ padding: "16px", fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>—</td>
                            <td style={{ padding: "16px", fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>—</td>
                            <td style={{ padding: "16px", fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>—</td>
                          </tr>
                        );
                      }

                      return records.map((record, index) => {
                        const display = formatRecordDisplay(record);
                        const { lock, hasSoftLock, hasHardLock, isPendingLock } = classifyLock(record);
                        return (
                          <tr
                            key={`${date}-${index}`}
                            onClick={() => handleRowClick(record)}
                            style={{
                              borderBottom: index === records.length - 1 ? "1px solid var(--ui-surface-recessed)" : "none",
                              transition: "background-color 0.2s",
                              cursor: "pointer",
                              backgroundColor: weekendBg,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "var(--ui-primary-tint)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = weekendBg;
                            }}
                          >
                            {index === 0 && (
                              <td
                                style={{ padding: "16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)", whiteSpace: "nowrap" }}
                                rowSpan={records.length}
                              >
                                {formatDateForTable(dateObj)}
                                <span style={{ fontSize: "12px", color: "var(--ui-text-faint)", fontWeight: "400", marginLeft: "8px" }}>{dayAbbr}</span>
                              </td>
                            )}
                            <td style={{ padding: "16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" }}>
                              {renderLockIcons({ lock, hasSoftLock, hasHardLock, isPendingLock })}
                              {display.timeIn}
                            </td>
                            <td style={{ padding: "16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" }}>
                              {display.timeOut}
                              {display.isOvernight && (
                                <span
                                  title={`Clocked out ${display.timeOutDate?.toLocaleDateString('en-US', { timeZone: 'Asia/Manila', month: 'short', day: '2-digit', year: 'numeric' })}`}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "0.625rem",
                                    fontWeight: "700",
                                    color: "var(--ui-warning-text)",
                                    background: "var(--ui-accent-tint)",
                                    borderRadius: "4px",
                                    padding: "1px 5px",
                                    marginLeft: "4px",
                                    verticalAlign: "middle",
                                    lineHeight: "1.3",
                                  }}
                                >
                                  +1
                                </span>
                              )}
                            </td>
                            <td style={{ padding: "16px", fontSize: "0.875rem" }}>
                              {display.dayFlag ? (
                                <DayFlagBadge flag={display.dayFlag} />
                              ) : (
                                <span style={{ color: "var(--ui-text-faint)" }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: "16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" }}>
                              {display.otHours}
                            </td>
                            <td style={{ padding: "16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" }}>
                              {display.nightDiff}
                            </td>
                            <td style={{ padding: "16px", fontSize: "0.875rem", fontWeight: "500", color: display.utHours !== '—' && display.utHours !== '0.00' ? "var(--ui-danger)" : "var(--ui-text)" }}>
                              {display.utHours}
                            </td>
                            <td style={{ padding: "16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" }}>
                              {display.regularHours}
                            </td>
                            <td style={{ padding: "16px", fontSize: "0.875rem", fontWeight: "500", color: "var(--ui-text)" }}>{display.break}</td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>

              {/* Table Footer */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "16px 20px",
                  background: "linear-gradient(135deg, var(--ui-bg) 0%, var(--ui-surface-recessed) 100%)",
                  borderTop: "1px solid var(--ui-border)",
                }}
              >
                {/* Eight stats since PRD_TimeRecordDayFlags C5 added Piece Rate,
                    Leave and Absent. The labels are nowrap: left to wrap, the
                    one-word ones (LEAVE, ABSENT) stayed on a single line while
                    the rest broke onto two, so their values sat a line higher
                    and the row of figures read ragged.
                    nowrap alone fixed 1280px but NOT ~1024px, where eight
                    unwrappable labels overran the bar: the footer sits outside
                    the table's overflowX:auto wrapper inside a card with
                    overflow:hidden, so the excess was CLIPPED, not scrolled —
                    Night Diff truncated, UT Hours and the hint gone. flexWrap
                    lets the group spill onto a second line instead, which is
                    what EmployeeList's footer already does with the identical
                    declaration (EmployeeList.js:542).
                    Do NOT reach for flexShrink:0 here: it pins a minimum width
                    inside that same clipping card and makes this strictly
                    worse. Gap stays 28px, the shared list-page standard
                    (DESIGN-STANDARDS.md:187). */}
                <div style={{ display: "flex", gap: "28px", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span style={{ fontSize: "11px", color: "var(--ui-text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
                      Total Days
                    </span>
                    <span data-testid="etr-footer-total-days" style={{ fontSize: "18px", fontWeight: "600", color: "var(--ui-primary)" }}>
                      {monthlyStats.totalDays}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span style={{ fontSize: "11px", color: "var(--ui-text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
                      Days Worked
                    </span>
                    <span data-testid="etr-footer-days-worked" style={{ fontSize: "16px", fontWeight: "600", color: "var(--ui-text)" }}>
                      {monthlyStats.daysWorked}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span style={{ fontSize: "11px", color: "var(--ui-text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
                      Piece Rate
                    </span>
                    <span data-testid="etr-footer-piece-rate" style={{ fontSize: "16px", fontWeight: "600", color: "var(--ui-text)" }}>
                      {monthlyStats.pieceRateDays}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span style={{ fontSize: "11px", color: "var(--ui-text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
                      Leave
                    </span>
                    <span data-testid="etr-footer-leave" style={{ fontSize: "16px", fontWeight: "600", color: "var(--ui-text)" }}>
                      {monthlyStats.leaveDays}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span style={{ fontSize: "11px", color: "var(--ui-text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
                      Absent
                    </span>
                    <span data-testid="etr-footer-absent" style={{ fontSize: "16px", fontWeight: "600", color: "var(--ui-text)" }}>
                      {monthlyStats.absentDays}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span style={{ fontSize: "11px", color: "var(--ui-text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
                      OT Hours
                    </span>
                    <span data-testid="etr-footer-ot-hours" style={{ fontSize: "16px", fontWeight: "600", color: "var(--ui-text)" }}>
                      {monthlyStats.totalOTHours}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span style={{ fontSize: "11px", color: "var(--ui-text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
                      Night Diff
                    </span>
                    <span data-testid="etr-footer-night-diff" style={{ fontSize: "16px", fontWeight: "600", color: "var(--ui-text)" }}>
                      {monthlyStats.totalNightDiff}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span style={{ fontSize: "11px", color: "var(--ui-text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
                      UT Hours
                    </span>
                    <span data-testid="etr-footer-ut-hours" style={{ fontSize: "16px", fontWeight: "600", color: "var(--ui-danger)" }}>
                      {monthlyStats.totalUtHours}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                    <span style={{ fontSize: "11px", color: "var(--ui-text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
                      Regular Hours
                    </span>
                    <span data-testid="etr-footer-regular-hours" style={{ fontSize: "16px", fontWeight: "600", color: "var(--ui-text)" }}>
                      {monthlyStats.totalRegularHours}
                    </span>
                  </div>
                </div>
                {/* marginLeft is NOT redundant with the row's space-between: when the
                    stats group wraps it fills its whole box, so there is zero free
                    space left for space-between to distribute and UT HOURS lands flush
                    against this hint. This outer row has no gap and no flexWrap of its
                    own (EmployeeList.js:536-539 uses both instead); the margin is what
                    stands in for them. Removed once on the reasoning above and restored
                    — the 1024px screenshot settled it. */}
                <span style={{ fontSize: "13px", fontStyle: "italic", color: "var(--ui-text-muted)", marginLeft: "24px" }}>
                  Click on any row to add or edit a record
                </span>
              </div>
            </div>

            {/* Mobile Cards View */}
            <div
              style={{
                display: isMobile ? "flex" : "none",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              {filteredDisplayData.map(({ date, dateObj, records }) => {
                const isEmpty = records.length === 0;
                // C5(a): same per-date lock as the desktop empty row.
                const emptyLock = isEmpty ? classifyDate(date) : null;
                const isMobileWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;

                return (
                  <div
                    key={date}
                    style={{
                      background: isMobileWeekend ? "var(--ui-surface-recessed)" : "white",
                      borderRadius: "8px",
                      padding: "1rem",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      opacity: isEmpty ? 0.5 : 1,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "0.75rem",
                        paddingBottom: "0.75rem",
                        borderBottom: "1px solid var(--ui-border)",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: "1rem", fontWeight: "600", color: "var(--ui-text)" }}>
                          {formatDateForTable(dateObj)}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)" }}>{getDayName(dateObj)}</div>
                      </div>
                    </div>

                    {isEmpty ? (
                      <div
                        data-testid={`etr-empty-card-${date}`}
                        onClick={emptyLock.hasHardLock ? undefined : () => handleAddTimeRecord(dateObj)}
                        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", cursor: emptyLock.hasHardLock ? "not-allowed" : "pointer" }}
                      >
                        {renderMobileLockLabels(emptyLock)}
                        <div>
                          <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>Time In</div>
                          <div style={{ fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>—</div>
                        </div>
                        <div>
                          <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>Time Out</div>
                          <div style={{ fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>—</div>
                        </div>
                        <div>
                          <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>Break</div>
                          <div style={{ fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>—</div>
                        </div>
                        <div>
                          <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>Regular Hours</div>
                          <div style={{ fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>—</div>
                        </div>
                        <div>
                          <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>OT Hours</div>
                          <div style={{ fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>—</div>
                        </div>
                        <div>
                          <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>Night Diff</div>
                          <div style={{ fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>—</div>
                        </div>
                        <div>
                          <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>UT Hours</div>
                          <div style={{ fontSize: "0.875rem", color: "var(--ui-text-faint)" }}>—</div>
                        </div>
                      </div>
                    ) : (
                      records.map((record, index) => {
                        const display = formatRecordDisplay(record);
                        const { lock, hasSoftLock, hasHardLock, isPendingLock } = classifyLock(record);
                        return (
                          <div
                            key={index}
                            onClick={() => handleRowClick(record)}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "0.75rem",
                              cursor: "pointer",
                              padding: "0.75rem",
                              borderRadius: "6px",
                              background: "transparent",
                              marginTop: index > 0 ? "0.75rem" : 0,
                            }}
                          >
                            {renderMobileLockLabels({ lock, hasSoftLock, hasHardLock, isPendingLock })}
                            {records.length > 1 && (
                              <div
                                style={{
                                  gridColumn: "1 / -1",
                                  fontSize: "0.875rem",
                                  fontWeight: "500",
                                  color: "var(--ui-text-muted)",
                                  marginBottom: "0.5rem",
                                  paddingBottom: "0.5rem",
                                  borderBottom: "1px dashed var(--ui-border)",
                                }}
                              >
                                Shift {index + 1}
                              </div>
                            )}
                            <div>
                              <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>Time In</div>
                              <div style={{ fontSize: "0.875rem", color: "var(--ui-text)", fontWeight: "500" }}>
                                {display.timeIn}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>Time Out</div>
                              <div style={{ fontSize: "0.875rem", color: "var(--ui-text)", fontWeight: "500" }}>
                                {display.timeOut}
                                {display.isOvernight && (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "0.625rem",
                                      fontWeight: "700",
                                      color: "var(--ui-warning-text)",
                                      background: "var(--ui-accent-tint)",
                                      borderRadius: "4px",
                                      padding: "1px 5px",
                                      marginLeft: "4px",
                                      verticalAlign: "middle",
                                      lineHeight: "1.3",
                                    }}
                                  >
                                    +1
                                  </span>
                                )}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>Flags</div>
                              <div style={{ fontSize: "0.875rem", fontWeight: "500" }}>
                                {display.dayFlag ? (
                                  <DayFlagBadge flag={display.dayFlag} />
                                ) : (
                                  <span style={{ color: "var(--ui-text-faint)" }}>—</span>
                                )}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>OT Hours</div>
                              <div style={{ fontSize: "0.875rem", color: "var(--ui-text)", fontWeight: "500" }}>
                                {display.otHours}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>Night Diff</div>
                              <div style={{ fontSize: "0.875rem", color: "var(--ui-text)", fontWeight: "500" }}>
                                {display.nightDiff}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>UT Hours</div>
                              <div style={{ fontSize: "0.875rem", color: display.utHours !== '—' && display.utHours !== '0.00' ? "var(--ui-danger)" : "var(--ui-text)", fontWeight: "500" }}>
                                {display.utHours}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>
                                Regular Hours
                              </div>
                              <div style={{ fontSize: "0.875rem", color: "var(--ui-text)", fontWeight: "500" }}>
                                {display.regularHours}
                              </div>
                            </div>
                            <div>
                              <div style={{ fontSize: "0.75rem", color: "var(--ui-text-muted)", fontWeight: "500" }}>Break</div>
                              <div style={{ fontSize: "0.875rem", color: "var(--ui-text)", fontWeight: "500" }}>
                                {display.break}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Add Time Record Modal */}
      <AddTimeRecordModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        selectedDate={selectedDateForAdd}
        companyId={company?.id}
        onSave={handleSaveSuccess}
        preSelectedEmployee={employee}
        source="employee-dtr"
        payrollRates={payrollRates}
        payrollRatesError={payrollRatesError}
        attendancePolicy={attendancePolicy}
        userDocId={user?.userData?.userDocId || user?.uid}
      />

      {/* Edit Time Record Modal */}
      <AddTimeRecordModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingRecord(null);
        }}
        selectedDate={editSelectedDate}
        companyId={company?.id}
        onSave={handleSaveSuccess}
        onDelete={handleDeleteRecord}
        preSelectedEmployee={employee}
        source="employee-dtr"
        mode="edit"
        recordToEdit={editingRecord}
        lockStatus={editingRecord ? (lockOf(editingRecord)?.status || null) : null}
        lockRunId={editingRecord ? (lockOf(editingRecord)?.runId || null) : null}
        payrollRates={payrollRates}
        payrollRatesError={payrollRatesError}
        attendancePolicy={attendancePolicy}
        userDocId={user?.userData?.userDocId || user?.uid}
      />
    </div>
  );
};